import { faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components/Flex';
import { Table } from 'components/Table';
import { P } from 'components/Typography/Typography';
import { Button } from 'components/_form';
import { PadlockButton } from 'components/_form/PadlockButton/PadlockButton';
import { UserSelect } from 'components/_form/UserSelect';
import { IOption } from 'components/_form/Select/Select';
import { dateFormat } from 'constants/dateFormats/dateFormat';
import { format } from 'date-fns';
import { useAccount } from 'hooks/useAccount';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IAuditsClosing } from 'types/auditsClosings';
import { IAssessmentsClosing } from 'types/assessmentsClosings';
import { IAuditFormsClosing } from 'types/auditFormsClosings';
import { handlePrevState } from 'utilities/handlePrevState';
import { makeUserOption } from 'utilities/makeUserOption';
import { onlyUniqueArray } from 'utilities/onlyUniqueArray';
import {
  IUserSelectOption,
  toUserSelectOption
} from 'utilities/userSelectOption';
import styled from 'styled-components';

const TableWrapper = styled.div`
  table {
    table-layout: fixed;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 120px;
    max-width: 120px;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 130px;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 48px;
  }
`;

const RoleCell = styled(P)`
  font-size: ${({ theme }) => theme.textStyles.body2.fontSize}px;
  line-height: 1.25;
  word-break: break-word;
`;

export interface UserOption extends IOption {
  role?: string;
}

interface Props {
  closingIds: string[];
  setClosingIds: (ids: string[]) => void;
  closings: (IAssessmentsClosing | IAuditsClosing | IAuditFormsClosing)[];
  usersList: IUserSelectOption[];
  userFunctionsList: IOption[];
  authorId?: string;
  authorizedEditorIds?: string[];
  authorizedEditorRoleLabel?: string;
  leaderId?: string;
  teamLeaderId?: string;
  teamManagerId?: string;
  participationsAttributes: {
    value: string;
    label: string | JSX.Element;
    function_id?: string;
  }[];
  handleClose: (id?: number) => void;
  handleOpen: (id?: number) => void;
  isAddingRowVisible: boolean;
  setIsAddingRowVisible: Dispatch<SetStateAction<boolean>>;
  allowRemoveAuthor?: boolean;
  allowAdminToggleClosing?: boolean;
}

export const ClosingTable: React.FC<Props> = ({
  closingIds,
  setClosingIds,
  closings,
  usersList,
  userFunctionsList,
  authorId,
  authorizedEditorIds,
  authorizedEditorRoleLabel,
  leaderId,
  teamLeaderId,
  teamManagerId,
  participationsAttributes,
  handleClose,
  handleOpen,
  isAddingRowVisible,
  setIsAddingRowVisible,
  allowRemoveAuthor = false,
  allowAdminToggleClosing = true
}) => {
  const { t } = useTranslation();

  const { loggedUserData } = useAccount();

  const isPadlockEnabled = (
    rowUserId: string,
    assessmentClosing?: IAssessmentsClosing | IAuditsClosing | IAuditFormsClosing
  ) =>
    !!assessmentClosing &&
    (rowUserId.toString() === loggedUserData.id.toString() ||
      (allowAdminToggleClosing && loggedUserData.role === 'admin'));

  // usersList = pracownicy firmy; Autor / lustro / closing.user mogą tam nie być → doklejamy fallback
  const effectiveUsersList = useMemo(() => {
    const byId = new Map<string, IUserSelectOption>(
      usersList.map((user) => [user.value.toString(), user])
    );

    const addFallback = (option?: IUserSelectOption) => {
      if (!option?.value) return;
      const id = option.value.toString();
      if (!byId.has(id)) byId.set(id, option);
    };

    closings.forEach((closing) => {
      if (closing.user) {
        addFallback(toUserSelectOption(closing.user));
      }
    });

    const loggedUserOption = toUserSelectOption({
      id: loggedUserData.id,
      first_name: loggedUserData.firstName,
      last_name: loggedUserData.lastName,
      email: loggedUserData.email
    });

    if (
      authorId &&
      authorId.toString() === loggedUserData.id.toString()
    ) {
      addFallback(loggedUserOption);
    }

    closingIds.forEach((closingId) => {
      if (closingId.toString() === loggedUserData.id.toString()) {
        addFallback(loggedUserOption);
      }
    });

    participationsAttributes.forEach((participant) => {
      if (!participant.value) return;
      const id = participant.value.toString();
      if (byId.has(id)) return;
      if (typeof participant.label === 'string' && participant.label) {
        addFallback({ value: id, label: participant.label });
      }
    });

    return Array.from(byId.values());
  }, [
    usersList,
    closings,
    authorId,
    closingIds,
    participationsAttributes,
    loggedUserData.id,
    loggedUserData.firstName,
    loggedUserData.lastName,
    loggedUserData.email
  ]);

  const usersOptions = useMemo(() => {
    const options = [
      makeUserOption({
        usersList: effectiveUsersList,
        userFunctionsList,
        userId: authorId,
        functionName: t('tasks.author')
      }),
      ...(authorizedEditorIds || [])
        .filter((editorId) => editorId !== authorId)
        .map((editorId) =>
          makeUserOption({
            usersList: effectiveUsersList,
            userFunctionsList,
            userId: editorId,
            functionName: authorizedEditorRoleLabel
          })
        ),
      makeUserOption({
        usersList: effectiveUsersList,
        userFunctionsList,
        userId: leaderId,
        functionName: t('createAssessmentView.leader')
      }),
      makeUserOption({
        usersList: effectiveUsersList,
        userFunctionsList,
        userId: teamLeaderId,
        functionName: t('tasks.teamLeader')
      }),
      makeUserOption({
        usersList: effectiveUsersList,
        userFunctionsList,
        userId: teamManagerId,
        functionName: t('tasks.teamManager')
      }),
      ...participationsAttributes
        .filter((participant) => participant.value)
        .map((participant) =>
          makeUserOption({
            usersList: effectiveUsersList,
            userFunctionsList,
            userId: participant.value,
            functionId: participant.function_id
          })
        ),
      ...closingIds.map((closingId) =>
        makeUserOption({
          usersList: effectiveUsersList,
          userFunctionsList,
          userId: closingId
        })
      )
    ].filter((option) => option) as UserOption[];

    return options.filter(
      (option, index, allOptions) =>
        allOptions.findIndex(
          ({ value }) => value.toString() === option.value.toString()
        ) === index
    );
  }, [
    effectiveUsersList,
    userFunctionsList,
    authorId,
    authorizedEditorIds,
    authorizedEditorRoleLabel,
    leaderId,
    teamLeaderId,
    teamManagerId,
    participationsAttributes,
    closingIds,
    t
  ]);

  const getSelectOptions = (currentId?: string) =>
    usersOptions.filter(
      (option) =>
        option.value.toString() === currentId?.toString() ||
        !closingIds.some(
          (closingId) => closingId.toString() === option.value.toString()
        )
    );

  const tableHeaders: string[] = [
    t('assessmentsView.assessmentStatusModal.table.headers.role'),
    t('assessmentsView.assessmentStatusModal.table.headers.person'),
    `${t('assessmentsView.assessmentStatusModal.table.headers.isClosed')}?`,
    ''
  ];

  const data = useMemo(
    () => [
      ...(closingIds.map((id) => {
        const assessmentClosing = closings.find(
          (closing) => closing.user?.id?.toString() === id.toString()
        );

        return {
          role: (
            <RoleCell variant="body">
              {usersOptions.find(
                ({ value }) => value.toString() === id.toString()
              )?.role || ''}
            </RoleCell>
          ),
          person: (
            <UserSelect
              disabled={!allowRemoveAuthor && authorId === id}
              withoutMargin
              options={getSelectOptions(id)}
              selectedOptions={usersOptions.find(
                ({ value }) => value.toString() === id.toString()
              )}
              onChange={(selectedOption) => {
                if (!Array.isArray(selectedOption)) {
                  setClosingIds(
                    onlyUniqueArray(
                      closingIds.map((prevId) =>
                        prevId === id ? selectedOption.value : prevId
                      )
                    )
                  );
                }
              }}
            />
          ),
          isClosed: (
            <Flex flexDirection="column" alignItems="center">
              <Flex
                flexDirection="row"
                justifyContent="space-around"
                width="100%"
              >
                <PadlockButton
                  disabled={!isPadlockEnabled(id, assessmentClosing)}
                  locked
                  active={!!assessmentClosing?.closed_at}
                  tooltipId={`closing-lock-${id}`}
                  title={t('common.lockEditing')}
                  onClick={() => {
                    handleClose(assessmentClosing?.id);
                  }}
                />
                <PadlockButton
                  disabled={!isPadlockEnabled(id, assessmentClosing)}
                  active={!assessmentClosing?.closed_at}
                  tooltipId={`closing-unlock-${id}`}
                  title={t('common.unlockEditing')}
                  onClick={() => {
                    handleOpen(assessmentClosing?.id);
                  }}
                />
              </Flex>
              <P variant="body" fontWeight={700}>
                {assessmentClosing?.closed_at &&
                  format(new Date(assessmentClosing?.closed_at), dateFormat)}
              </P>
            </Flex>
          ),
          options: (
            <Button
              disabled={
                (!allowRemoveAuthor && authorId === id) ||
                !!assessmentClosing?.closed_at
              }
              onClick={() => {
                setClosingIds(handlePrevState(closingIds, id));
              }}
              variant="redWhite"
              bordered
              transparent
              icon={<FontAwesomeIcon icon={faUserSlash} />}
            />
          )
        };
      }) || []),
      ...(isAddingRowVisible
        ? [
            {
              role: '',
              person: (
                <UserSelect
                  withoutMargin
                  isMulti={false}
                  options={getSelectOptions()}
                  onChange={(selectedOption) => {
                    if (!Array.isArray(selectedOption)) {
                      setClosingIds(
                        onlyUniqueArray(
                          handlePrevState(closingIds, selectedOption.value)
                        )
                      );
                      setIsAddingRowVisible(false);
                    }
                  }}
                />
              ),
              isClosed: (
                <Flex flexDirection="row" justifyContent="space-around"></Flex>
              )
            }
          ]
        : [])
    ],
    [
      closingIds,
      isAddingRowVisible,
      usersOptions,
      closings,
      loggedUserData.id,
      loggedUserData.role,
      allowAdminToggleClosing,
      authorId,
      allowRemoveAuthor,
      authorizedEditorIds,
      authorizedEditorRoleLabel,
      setClosingIds,
      handleClose,
      handleOpen,
      setIsAddingRowVisible
    ]
  );

  return (
    <TableWrapper>
      <Table header={tableHeaders} data={data} sortTable={false} />
    </TableWrapper>
  );
};
